import { useEffect, useState } from "react";
import { Alert, Button, Card, Empty, Form, Input, Modal, Select, Space, Tag, Typography, message } from "antd";
import dayjs from "dayjs";
import { Mail, Plus, Send, Trash2 } from "lucide-react";
import axiosClient from "../../api/axiosClient";
import { ComingSoonTag } from "../../utils/comingSoon";

const { Text } = Typography;

interface ScheduledReport {
  schedule_id: string;
  name: string;
  report_type: string;
  recipients: string[];
  frequency: string;
  last_sent_at?: string | null;
}

const REPORT_TYPE_LABELS: Record<string, string> = {
  revenue_summary: "Revenue Summary",
  staff_performance: "Staff Performance",
  client_retention: "Client Retention",
};

export default function ReportsScheduledTab() {
  const [reports, setReports] = useState<ScheduledReport[]>([]);
  const [loading, setLoading] = useState(true);
  const [modalOpen, setModalOpen] = useState(false);
  const [sendingId, setSendingId] = useState<string | null>(null);
  const [form] = Form.useForm();

  function fetchReports() {
    setLoading(true);
    axiosClient.get("/scheduled-reports").then((res) => setReports(res.data ?? [])).catch(() => message.error("Failed to load scheduled reports")).finally(() => setLoading(false));
  }

  useEffect(() => { fetchReports(); }, []);

  async function saveReport() {
    const vals = await form.validateFields();
    try {
      await axiosClient.post("/scheduled-reports", { ...vals, recipients: vals.recipients.split(",").map((s: string) => s.trim()).filter(Boolean) });
      message.success("Scheduled report created");
      setModalOpen(false);
      form.resetFields();
      fetchReports();
    } catch {
      message.error("Failed to create scheduled report");
    }
  }

  async function removeReport(id: string) {
    try {
      await axiosClient.delete(`/scheduled-reports/${id}`);
      fetchReports();
    } catch {
      message.error("Failed to delete");
    }
  }

  async function sendNow(id: string) {
    setSendingId(id);
    try {
      await axiosClient.post(`/scheduled-reports/${id}/send-now`);
      message.success("Report sent");
      fetchReports();
    } catch (err: any) {
      message.error(err?.response?.status === 500 ? "Email send failed — this dev environment's SES sender may not be verified yet" : "Failed to send report");
    } finally {
      setSendingId(null);
    }
  }

  return (
    <div>
      <Alert
        style={{ marginBottom: 16 }}
        type="info"
        showIcon
        message="Automatic sending is coming soon"
        description="Saved reports are sent when you click “Send Now”. Sending on a daily, weekly or monthly schedule isn't available yet."
      />
      <div style={{ display: "flex", justifyContent: "space-between", marginBottom: 16 }}>
        <Text type="secondary">{reports.length} scheduled report{reports.length !== 1 ? "s" : ""}</Text>
        <Button type="primary" icon={<Plus size={14} />} onClick={() => { form.resetFields(); setModalOpen(true); }}>New Schedule</Button>
      </div>

      {!loading && reports.length === 0 ? (
        <Empty description="No scheduled reports yet" />
      ) : (
        <Space direction="vertical" size={12} style={{ width: "100%" }}>
          {reports.map((r) => (
            <Card key={r.schedule_id} size="small">
              <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                <div>
                  <Space>
                    <Text strong>{r.name}</Text>
                    <Tag>{REPORT_TYPE_LABELS[r.report_type] ?? r.report_type}</Tag>
                  </Space>
                  <div style={{ fontSize: 12, color: "#8c8c8c", marginTop: 4, display: "flex", alignItems: "center", gap: 6 }}>
                    <Mail size={12} /> {r.recipients.join(", ")}
                  </div>
                  <div style={{ fontSize: 12, color: "#8c8c8c" }}>
                    {r.last_sent_at ? `Last sent ${dayjs(r.last_sent_at).format("MMM D, h:mm A")}` : "Never sent"}
                  </div>
                </div>
                <Space>
                  <Button size="small" icon={<Send size={13} />} loading={sendingId === r.schedule_id} onClick={() => sendNow(r.schedule_id)}>Send Now</Button>
                  <Button size="small" type="text" danger icon={<Trash2 size={13} />} onClick={() => removeReport(r.schedule_id)} />
                </Space>
              </div>
            </Card>
          ))}
        </Space>
      )}

      <Modal title="New Scheduled Report" open={modalOpen} onCancel={() => setModalOpen(false)} onOk={saveReport} okText="Create">
        <Form form={form} layout="vertical" style={{ marginTop: 16 }} initialValues={{ frequency: "weekly", report_type: "revenue_summary" }}>
          <Form.Item name="name" label="Name" rules={[{ required: true, message: "Required" }]}>
            <Input placeholder="e.g. Weekly Revenue Email" />
          </Form.Item>
          <Form.Item name="report_type" label="Report">
            <Select options={Object.entries(REPORT_TYPE_LABELS).map(([v, label]) => ({ value: v, label }))} />
          </Form.Item>
          <Form.Item name="frequency" label={<span>Frequency <ComingSoonTag /></span>}>
            <Select disabled options={[{ value: "daily", label: "Daily" }, { value: "weekly", label: "Weekly" }, { value: "monthly", label: "Monthly" }]} />
          </Form.Item>
          <Form.Item name="recipients" label="Recipients (comma-separated emails)" rules={[{ required: true, message: "Required" }]}>
            <Input placeholder="owner@salon.com, manager@salon.com" />
          </Form.Item>
        </Form>
      </Modal>
    </div>
  );
}
